/**
 * Campaign API endpoints for Lobby2.0
 * Following the same pattern as tournaments.api.ts
 */

import {
  REBATE_CAMPAIGN_TYPE,
  PRIZE_POP_CAMPAIGN_TYPE,
  TOURNAMENTS_CAMPAIGN_TYPE
} from 'features/campaigns';
import type {
  CampaignSource,
  CampaignsResponse,
  CampaignTypes,
  ICampaignPrizesResponse,
  ICampaignWinsResponse,
} from 'features/campaigns';
import type { ICampaignConfig } from 'features/campaigns/configs/campaignConfig.types';
import { 
  buildMainFetchUrl,
  buildSubFetchUrl
} from 'features/campaigns/configs/configUtils';
import { buildResourceUrls, isLocalAssetsHost } from '../buildResourceUrls';

export interface ICampaignSessionData {
  sessionKey: string;
  playerId: string;
  gameKey?: string;
}

export interface IJoinCampaignProps {
  config: ICampaignConfig;
  host: string;
  sessionInfo: ICampaignSessionData;
  data: string[];
}

export interface IJoinCampaignResponse {
  id: string;
  errorCode: number | null;
  joined: boolean;
}

export interface IGetCampaignPrizesProps {
  config: ICampaignConfig;
  host: string;
  campaignId: string;
  sessionInfo?: ICampaignSessionData;
}

export interface IGetCampaignWinsProps {
  config: ICampaignConfig;
  host: string;
  campaignId: string;
  sessionInfo?: ICampaignSessionData;
}

type FetchCampaignsSearchParamsType = Record<string, string> & ICampaignSessionData & {
  stripResourcesHost?: 'true';
}

/**
 * Fetches all campaigns for the player.
 *
 * `stripResourcesHost=true` tells the backend that this client knows how to
 * assemble asset URLs itself, so it can return relative `resources.*` paths.
 * Older clients omit the flag and keep getting full URLs (back-compat).
 *
 * On local dev the template assets aren't served by the local assets server,
 * so we keep `stripResourcesHost=false` and use the full CDN URLs the backend
 * already returns instead of rewriting relative paths against `localhost`.
 */
export const fetchCampaigns = async (
  config: ICampaignConfig,
  host: string,
  assetsHost: string,
  sessionInfo: ICampaignSessionData
): Promise<CampaignsResponse> => {
  const url = buildMainFetchUrl(config, { host });
  // On local dev omit the flag (back-compat path) so the backend returns full
  // CDN URLs — the local assets server doesn't serve the template assets.
  const params: FetchCampaignsSearchParamsType = { ...sessionInfo };
  if (!isLocalAssetsHost(assetsHost)) params.stripResourcesHost = 'true';
  const queryString = new URLSearchParams(params).toString();
  const fullUrl = `${url}?${queryString}`;

  const response = await fetch(fullUrl);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ 
      message: 'Failed to fetch campaigns' 
    }));
    throw new Error(errorData.message || `Failed to fetch campaigns: ${response.statusText}`);
  }

  const data = await response.json();
  // Normalize: each campaign API returns its array under a different key
  // (prize-pop → data.campaigns, tournaments → data.tournaments, rebate → data.campaigns).
  const responseArrayKey = {
    [PRIZE_POP_CAMPAIGN_TYPE]: 'campaigns',
    [TOURNAMENTS_CAMPAIGN_TYPE]: 'tournaments',
    [REBATE_CAMPAIGN_TYPE]: 'campaigns',
  } as Record<CampaignTypes, string>;

  const rawList = (data as Record<string, { resources?: Record<string, unknown> }[]>)[responseArrayKey[config.type]] ?? [];

  return {
    ...data,
    campaigns: rawList.map((campaign) => ({
      ...buildResourceUrls(campaign, assetsHost),
      type: config.type,
    })) as CampaignSource[],
  };
};

export const joinCampaign = async ({ config, host, sessionInfo, data }: IJoinCampaignProps): Promise<IJoinCampaignResponse[]> => {
  const joinFetch = config.subFetches['join'];
  if (!joinFetch) {
    throw new Error('Join fetch not configured for this campaign type');
  }
  
  if (!joinFetch.buildJoinInRequestData) {
    throw new Error('BuildJoinInRequestData function is not configured for this campaign type');
  }

  const url = buildSubFetchUrl(joinFetch, { host });
  const { fullUrl, requestBody } = joinFetch.buildJoinInRequestData({sessionInfo, url, data});

  const response = await fetch(fullUrl, {
    method: joinFetch.method || 'POST',
    body: JSON.stringify(requestBody),
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to join campaign' }));
    throw new Error(errorData.message || `Failed to join campaign: ${response.statusText}`);
  }

  const responseData = await response.json() as IJoinCampaignResponse[];
  responseData.forEach(joinData => {
    if (joinData.errorCode) {
      throw new Error(`Campaign join failed with error code ${joinData.errorCode}`);
    }
  });

  return responseData;
};

/**
 * Fetches prizes for a specific campaign
 */
export const fetchCampaignPrizes = async ({
  config,
  host,
  campaignId,
  sessionInfo
}: IGetCampaignPrizesProps): Promise<ICampaignPrizesResponse> => {
  const prizesFetch = config.subFetches['prizes'];
  if (!prizesFetch) {
    throw new Error('Prizes fetch not configured for this campaign type');
  }

  let url = buildSubFetchUrl(prizesFetch, { host, campaignId });
  
  if (sessionInfo) {
    url += '?' + new URLSearchParams(sessionInfo as FetchCampaignsSearchParamsType).toString();
  }
  
  const response = await fetch(url);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ 
      message: 'Failed to fetch campaign prizes' 
    }));
    throw new Error(errorData.message || `Failed to fetch campaign prizes: ${response.statusText}`);
  }

  return response.json() as Promise<ICampaignPrizesResponse>;
};

/**
 * Fetches wins/winners for a specific campaign
 */
export const fetchCampaignWins = async ({
  config,
  host,
  campaignId,
  sessionInfo
}: IGetCampaignWinsProps): Promise<ICampaignWinsResponse> => {
  const winsFetch = config.subFetches['wins'];
  if (!winsFetch) {
    throw new Error('Wins fetch not configured for this campaign type');
  }

  let url = buildSubFetchUrl(winsFetch, { host, campaignId });
  
  if (sessionInfo) {
    url += '?' + new URLSearchParams(sessionInfo as FetchCampaignsSearchParamsType).toString();
  }
  
  const response = await fetch(url);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ 
      message: 'Failed to fetch campaign wins' 
    }));
    throw new Error(errorData.message || `Failed to fetch campaign wins: ${response.statusText}`);
  }

  const data = await response.json();

  return {
    wins: data.playerWins || data.wins || [],
    totalWins: data.totalWins ?? 0,
  } as ICampaignWinsResponse;
};
